import { useEffect, useState, useCallback } from "react";
import { Stack } from "@altimateai/lego";
import { DownloadTable } from "@/components/downloadTable";
import { FilterApplicableFor } from "@/components/tags/types";
import { useColumnPickerContext } from "@/components/column/types";
import { setApplicableFor } from "@/components/column/columnPickerSlice";
import { downloadOpportunitiesCSV } from "@/helpers/apis";
import { useExplorerFiltersFromUrl } from "../../hooks";
import type { GetOpportunitiesDataResponse, Page, OwnerListItem, SelectedRow } from "../../types";
import { OpportunitiesTable } from "./OpportunitiesTable";
import { OpportunitiesBulkActions } from "./OpportunitiesBulkActions";

interface OpportunitiesSectionProps {
  data: Page<GetOpportunitiesDataResponse>;
}

export const OpportunitiesSection = ({ data }: OpportunitiesSectionProps) => {
  const { filterParams } = useExplorerFiltersFromUrl();
  const { dispatch } = useColumnPickerContext();

  // Local state for row selection
  const [selectedRows, setSelectedRows] = useState<SelectedRow[]>([]);

  // Local state for owner list (for optimistic updates)
  const [ownerList, setOwnerList] = useState<OwnerListItem[] | null>(null);

  useEffect(() => {
    dispatch(setApplicableFor(FilterApplicableFor.DISCOVER));
  }, [dispatch]);

  // Initialize owner list from data if not set
  useEffect(() => {
    if (!ownerList && data?.items) {
      setOwnerList(
        data.items.map(item => ({
          opportunity_id: item.opportunity_id,
          owner: item.owner ?? "",
        }))
      );
    }
  }, [data, ownerList]);

  const handleDownloadClick = async () => {
    await downloadOpportunitiesCSV(filterParams);
  };

  const handleSelectionChanged = useCallback((rows: SelectedRow[]) => {
    setSelectedRows(rows);
  }, []);

  const handleOwnerChange = useCallback((owner: number | string, opportunity_id: string) => {
    setOwnerList(
      prev =>
        prev?.map(item => (item.opportunity_id === opportunity_id ? { ...item, owner } : item)) ??
        null
    );
  }, []);

  const handleBulkApplySuccess = useCallback((newOwnerList: OwnerListItem[]) => {
    setOwnerList(newOwnerList);
    setSelectedRows([]);
  }, []);

  const handleCancelSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  return (
    <Stack direction={"column"} gap={"lg"} align={"start"} className={"al-relative al-w-full"}>
      <div className={"al-absolute al-right-0"} style={{ top: "-3rem" }}>
        <DownloadTable handleDownload={handleDownloadClick} buttonProps={{ size: "sm" }} />
      </div>
      <OpportunitiesBulkActions
        visible={selectedRows.length > 0}
        selectedRows={selectedRows}
        ownerList={ownerList}
        onApplySuccess={handleBulkApplySuccess}
        onCancel={handleCancelSelection}
      />
      <div className={"al-w-full"}>
        <OpportunitiesTable
          data={data}
          ownerList={ownerList}
          onSelectionChanged={handleSelectionChanged}
          onOwnerChange={handleOwnerChange}
        />
      </div>
    </Stack>
  );
};
